import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download, Info, MapPin, Pencil, Plus, Power, Trash2, Upload, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { useNotice } from '../../../../hooks/useNotice';
import { OccupancyCell } from './components/OccupancyCell';
import { SlotFormModal } from './modals/SlotFormModal';
import { SlotsUploadModal } from './modals/SlotsUploadModal';
import { deleteSlot, fetchSlots, updateSlot } from '../../../../features/slots/slotsThunks';
import { setSlotFilters } from '../../../../features/slots/slotsSlice';
import { ALL_ZONES, ZONE_FILTER_OPTIONS } from '../../../../features/slots/slotsConstants';
import {
  buildSlotsExportRows,
  slotsExportFileName,
} from '../../../../features/slots/slotsUtils';
import {
  selectCurrentWarehouse,
  selectWarehouses,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { zoneTypeLabel } from '../../../../utils/inventoryLabels';

/**
 * `/warehouse/locations`: the positions of a warehouse and how much is in each.
 *
 * Reading is wider than configuring. Anybody who reaches a warehouse sees its
 * locations; only its **holder** creates, edits or removes them, and the server
 * says who that is with `can_manage` — nothing here works it out. With "todas
 * mis bodegas" the listing runs across all of them and nothing can be created:
 * that is not a place to put a rack in.
 */
export const Locations = () => {
  const dispatch = useDispatch();

  const { slots, loading, error, filters, busyId } = useSelector((state) => state.slots);
  const warehouse = useSelector(selectCurrentWarehouse);
  const warehouses = useSelector(selectWarehouses);

  const [searchInput, setSearchInput] = useState(filters.search);
  /** `null` closed, `{ slot: null }` a new one, `{ slot }` an edit. */
  const [form, setForm] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const setNotice = useNotice('locations-notice');

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setSlotFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when the warehouse in use or a filter the server applies changes
  useEffect(() => {
    dispatch(fetchSlots());
  }, [dispatch, warehouse?.id, filters.search, filters.zoneType]);


  const canManage = Boolean(warehouse?.can_manage);
  const showWarehouse = !warehouse && warehouses.length > 1;

  const handleToggleActive = async (slot) => {
    const action = await dispatch(
      updateSlot({ slotId: slot.id, values: { is_active: !slot.is_active } })
    );
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? {
            type: 'success',
            message: slot.is_active
              ? `${slot.name} quedó de baja: conserva su historial y deja de recibir.`
              : `${slot.name} volvió a estar activa.`,
          }
        : { type: 'error', message: action.payload }
    );
  };

  const handleDelete = async () => {
    const action = await dispatch(deleteSlot(toDelete.id));
    setToDelete(null);
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? { type: 'success', message: 'Ubicación eliminada.' }
        : { type: 'error', message: action.payload }
    );
  };

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildSlotsExportRows(slots));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Ubicaciones');
    XLSX.writeFile(book, slotsExportFileName(warehouse));
  };

  // The server sends every location that matches; the table pages them here.
  const lastPage = Math.max(1, Math.ceil(slots.length / filters.rowsPerPage));
  const page = Math.min(filters.page, lastPage);
  const rows = slots.slice((page - 1) * filters.rowsPerPage, page * filters.rowsPerPage);

  const columns = [
    ...(showWarehouse
      ? [
          {
            header: 'BODEGA',
            cell: (row) => <span className="text-text-secondary">{row.warehouse_name}</span>,
          },
        ]
      : []),
    {
      header: 'UBICACIÓN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.name}</span>
          {/* The scannable code only when it says something the name does not. */}
          {row.code && row.code !== row.name && (
            <span className="text-xs text-text-muted mt-0.5">{row.code}</span>
          )}
        </div>
      ),
    },
    {
      // The walking order belongs with the zone: both say where the location
      // stands in the route of whoever picks.
      header: 'ZONA',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-ninja-dark dark:text-white">{zoneTypeLabel(row.zone_type)}</span>
          <span className="text-xs text-text-muted mt-0.5">
            {row.pick_rank ? `Recorrido ${row.pick_rank}` : 'Sin orden de recorrido'}
          </span>
        </div>
      ),
    },
    { header: 'OCUPACIÓN', cell: (row) => <OccupancyCell slot={row} /> },
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={row.is_active ? 'success' : 'neutral'} testId="slot-status">
          {row.is_active ? 'Activa' : 'De baja'}
        </ToneBadge>
      ),
    },
    // What cannot be used is not drawn: without the holder there is no column.
    ...(canManage
      ? [
          {
            header: 'ACCIONES',
            cell: (row) => {
              const isBusy = busyId === row.id;
              return (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="subtle"
                    size="sm"
                    iconOnly
                    icon={Pencil}
                    title="Editar"
                    aria-label={`Editar ${row.name}`}
                    data-testid="slot-edit"
                    disabled={isBusy}
                    onClick={() => setForm({ slot: row })}
                  />
                  <Button
                    variant="subtle"
                    size="sm"
                    iconOnly
                    tone={row.is_active ? 'danger' : 'success'}
              icon={row.is_active ? X : Power}
                    loading={isBusy}
                    title={
                      row.is_active
                        ? 'Dar de baja: conserva el historial y deja de recibir'
                        : 'Reactivar'
                    }
                    aria-label={`${row.is_active ? 'Dar de baja' : 'Reactivar'} ${row.name}`}
                    data-testid="slot-toggle-active"
                    onClick={() => handleToggleActive(row)}
                  />
                  <Button
                    variant="subtle"
                    size="sm"
                    iconOnly
                    icon={Trash2}
                    title={
                      row.can_delete
                        ? 'Eliminar: nunca pasó mercadería por acá'
                        : 'Tiene mercadería o historial en el kardex. Dala de baja en vez de eliminarla'
                    }
                    aria-label={`Eliminar ${row.name}`}
                    data-testid="slot-delete"
                    className="hover:text-danger"
                    disabled={!row.can_delete || isBusy}
                    onClick={() => setToDelete(row)}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  // An empty table has to say why: empty looks the same as broken.
  const isFiltering = filters.search !== '' || filters.zoneType !== ALL_ZONES;
  const emptyMessage = (() => {
    if (isFiltering) return 'No hay ubicaciones que coincidan con estos filtros.';
    if (!warehouse) return 'Tus bodegas todavía no tienen ubicaciones.';
    return canManage
      ? `${warehouse.name} todavía no tiene ubicaciones. Crea la primera para poder recibir mercadería.`
      : `${warehouse.name} todavía no tiene ubicaciones.`;
  })();

  return (
    <div data-testid="locations-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={MapPin}
        title="Ubicaciones"
        description="Las posiciones de la bodega y cuánto hay en cada una. La ocupación sale del kardex, no de un contador aparte."
        actions={
          // Drawn for whoever could use it somewhere, and disabled — saying
          // why — until a warehouse is picked. Not drawn at all in a warehouse
          // held by somebody else.
          (!warehouse || canManage) && (
            <Button
              variant="primary"
              icon={Plus}
              disabled={!canManage}
              title={
                canManage ? undefined : 'Elige arriba la bodega donde quieres crear la ubicación'
              }
              onClick={() => setForm({ slot: null })}
              data-testid="slot-add"
            >
              Nueva ubicación
            </Button>
          )
        }
      />

      {warehouse && !canManage && (
        <p
          data-testid="locations-readonly-note"
          className="flex items-center gap-2 text-sm text-text-secondary"
        >
          <Info size={16} className="text-info flex-shrink-0" />
          Guardas mercadería en {warehouse.name}, pero el espacio no es tuyo: puedes ver sus
          ubicaciones y no modificarlas.
        </p>
      )}

      <FilterBar
        variant="bare"
        testId="locations-filters"
        actions={
          <>
            {/* A hundred locations are a sheet, not a hundred forms. Same
                place and look as the catalogue's: next to the export. Not
                drawn at all in a warehouse held by somebody else. */}
            {(!warehouse || canManage) && (
              <Button
                variant="ghost"
                icon={Upload}
                disabled={!canManage}
                title={
                  canManage
                    ? 'Varias ubicaciones de una vez, desde una planilla'
                    : 'Elige arriba la bodega donde quieres cargar las ubicaciones'
                }
                onClick={() => setIsUploadOpen(true)}
                data-testid="slots-upload"
              >
                Carga masiva
              </Button>
            )}
            <Button
              variant="ghost"
              icon={Download}
              onClick={handleExport}
              disabled={slots.length === 0}
              title={
                slots.length > 0
                  ? 'Excel de las ubicaciones que coinciden con los filtros'
                  : 'No hay ubicaciones que exportar'
              }
              data-testid="locations-export"
            >
              Exportar
            </Button>
          </>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por ubicación o código..."
          testId="locations-search"
        />
        <FilterableSelect
          label="Zona:"
          value={filters.zoneType}
          onChange={(zoneType) => dispatch(setSlotFilters({ zoneType }))}
          options={ZONE_FILTER_OPTIONS}
          testId="locations-zone"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={rows}
        totalItems={slots.length}
        currentPage={page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchSlots())}
        itemLabel="ubicaciones"
        emptyMessage={emptyMessage}
        onPageChange={(nextPage) => dispatch(setSlotFilters({ page: nextPage }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setSlotFilters({ rowsPerPage }))}
      />

      <SlotFormModal
        isOpen={Boolean(form)}
        onClose={() => setForm(null)}
        warehouse={warehouse}
        slot={form?.slot ?? null}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

      <SlotsUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        warehouse={warehouse}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title={toDelete ? `Eliminar ${toDelete.name}` : ''}
        message="Nunca pasó mercadería por esta ubicación, así que se puede eliminar sin dejar huecos en el kardex. No se puede deshacer."
        loading={Boolean(toDelete) && busyId === toDelete.id}
        testId="slot-delete-dialog"
      />

    </div>
  );
};
